<template>
  <div class="new project h-auto p-3 pb-10">
    <nav class="flex flex-col" aria-label="Breadcrumb">
      <ol class="inline-flex items-center space-x-1 md:space-x-3">
        <li class="inline-flex items-center">
          <router-link
            to="/"
            class="inline-flex items-center text-sm font-medium text-gray-700 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
          >
            <svg
              class="mr-2 w-4 h-4"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"
              ></path>
            </svg>
            Home
          </router-link>
        </li>
        <li>
          <div class="flex items-center">
            <svg
              class="w-6 h-6 text-gray-400"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clip-rule="evenodd"
              ></path>
            </svg>
            <router-link
              to="/new/add"
              class="ml-1 text-sm font-medium text-gray-700 hover:text-gray-900 md:ml-2 dark:text-gray-400 dark:hover:text-white"
              >Concepts | Add Concept</router-link
            >
          </div>
        </li>
      </ol>

      <div class="mt-5 w-full">
        <h1 class="text-2xl text-gray-900 font-medium dark:text-gray-200">Add Concept</h1>
        <p class="mt-1 text-sm font-normal text-gray-400">
          Add your new concept informations.
        </p>
      </div>
    </nav>

    <div
      class="bg-white dark:bg-gray-800 rounded-md p-5 w-full box-border border dark:border-gray-700 mt-5"
    >
      <form @submit.prevent="addConcept">
        <div class="informations flex flex-col gap-6 mb-5">
          <!-- Concept Title -->
          <div class="flex flex-col gap-2">
            <div class="flex flex-col gap-1 mb-3">
              <h3 class="text-lg text-primary font-medium">Concept Title</h3>
              <hr class="border-none w-full h-[2px] bg-gray-200 dark:bg-gray-600" />
            </div>
            <Input
              type="text"
              placeholder="Title"
              v-model="addConceptViewModel.concept.title"
              :isRequired="true"
            ></Input>
          </div>

          <!-- Header Slider Section -->
          <div class="flex flex-col gap-2">
            <div class="flex flex-col gap-1 mb-3">
              <h3 class="text-lg text-primary font-medium">Header Slider</h3>
              <hr class="border-none w-full h-[2px] bg-gray-200 dark:bg-gray-600" />
            </div>
            <div>
              <div class="space-y-4">
                <div
                  class="flex flex-col gap-2"
                  v-for="(item, index) in addConceptViewModel.sliderPictures"
                  :key="index"
                >
                  <div class="flex justify-between items-end">
                    <h6 class="font-medium text-sm text-gray-600 dark:text-gray-400">
                      {{ `Slider Item ${index + 1}` }}
                    </h6>

                    <button
                      v-if="index > 0"
                      @click="() => deleteSliderItem(index)"
                      class="text-primary text-base border border-primary rounded-full p-1"
                      type="button"
                    >
                      <Icon icon="tabler:trash-filled" />
                    </button>
                  </div>
                  <input
                    type="text"
                    class="border-2 rounded-md overflow-hidden border-gray-200 dark:border-gray-700 box-border flex p-3 w-full text-black dark:text-white bg-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 dark:bg-gray-900 outline-none"
                    placeholder="Slider Text"
                    v-model="addConceptViewModel.sliderPictures[index].title"
                    required
                  />
                  <FileUploader
                    @updatePendingFiles="updatePendingFiles($event, 'slider', index)"
                  ></FileUploader>
                </div>
              </div>

              <div class="flex mt-3 justify-center">
                <button
                  v-if="addConceptViewModel.sliderPictures.length < 4"
                  type="button"
                  @click="addSlider"
                  class="text-2xl text-white bg-primary rounded-md hover:bg-primary/80 transition-all duration-300 focus:outline-none py-2 px-6 w-fit"
                >
                  <Icon icon="ic:outline-plus"></Icon>
                </button>
              </div>
            </div>
          </div>

          <!-- Project Section -->
          <div class="flex flex-col gap-4">
            <div class="flex flex-col gap-1 mb-1">
              <h3 class="text-lg text-primary font-medium">Project Section</h3>
              <hr class="border-none w-full h-[2px] bg-gray-200 dark:bg-gray-600" />
            </div>
            <div class="flex flex-col gap-2 w-full">
              <h6 class="font-medium text-sm text-gray-600 dark:text-gray-400">
                Project Image
              </h6>
              <FileUploader
                @updatePendingFiles="updatePendingFiles($event, 'projectImage')"
              ></FileUploader>
            </div>

            <div class="flex flex-col gap-2">
              <Input
                type="textarea"
                placeholder="Project Text"
                v-model="addConceptViewModel.concept.projectText"
                :isRequired="true"
              ></Input>
            </div>
          </div>

          <!-- Best Features Section -->
          <div class="flex flex-col gap-2">
            <div class="flex flex-col gap-1 mb-3">
              <h3 class="text-lg text-primary font-medium">Features Section</h3>
              <hr class="border-none w-full h-[2px] bg-gray-200 dark:bg-gray-600" />
            </div>

            <div class="grid xl:grid-cols-3 grid-cols-1 xl:gap-5 gap-4">
              <!-- Box 1 -->
              <div class="flex flex-col gap-2">
                <h6 class="font-medium text-sm text-gray-600 dark:text-gray-400">
                  Box 1
                </h6>

                <div class="flex flex-col gap-2">
                  <FileUploader
                    @updatePendingFiles="updatePendingFiles($event, 'featuresBoxImage1')"
                  ></FileUploader>

                  <input
                    type="text"
                    class="border-2 rounded-md overflow-hidden border-gray-200 dark:border-gray-700 box-border flex p-3 w-full text-black dark:text-white bg-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 dark:bg-gray-900 outline-none"
                    placeholder="Title"
                    v-model="
                      addConceptViewModel.concept.featuresBoxes.bestFeaturesBoxOneTitle
                    "
                    required
                  />

                  <textarea
                    class="border-2 rounded-md overflow-hidden border-gray-200 dark:border-gray-700 box-border flex p-3 w-full text-black dark:text-white bg-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 dark:bg-gray-900 outline-none"
                    placeholder="Description"
                    v-model="
                      addConceptViewModel.concept.featuresBoxes
                        .bestFeaturesBoxOneDescription
                    "
                    required
                  ></textarea>
                </div>
              </div>

              <!-- Box 2 -->
              <div class="flex flex-col gap-2">
                <h6 class="font-medium text-sm text-gray-600 dark:text-gray-400">
                  Box 2
                </h6>

                <div class="flex flex-col gap-2">
                  <FileUploader
                    @updatePendingFiles="updatePendingFiles($event, 'featuresBoxImage2')"
                  ></FileUploader>

                  <input
                    type="text"
                    class="border-2 rounded-md overflow-hidden border-gray-200 dark:border-gray-700 box-border flex p-3 w-full text-black dark:text-white bg-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 dark:bg-gray-900 outline-none"
                    placeholder="Title"
                    v-model="
                      addConceptViewModel.concept.featuresBoxes.bestFeaturesBoxTwoTitle
                    "
                    required
                  />

                  <textarea
                    class="border-2 rounded-md overflow-hidden border-gray-200 dark:border-gray-700 box-border flex p-3 w-full text-black dark:text-white bg-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 dark:bg-gray-900 outline-none"
                    placeholder="Description"
                    v-model="
                      addConceptViewModel.concept.featuresBoxes
                        .bestFeaturesBoxTwoDescription
                    "
                    required
                  ></textarea>
                </div>
              </div>

              <!-- Box 3 -->
              <div class="flex flex-col gap-2">
                <h6 class="font-medium text-sm text-gray-600 dark:text-gray-400">
                  Box 3
                </h6>

                <div class="flex flex-col gap-2">
                  <FileUploader
                    @updatePendingFiles="updatePendingFiles($event, 'featuresBoxImage3')"
                  ></FileUploader>

                  <input
                    type="text"
                    class="border-2 rounded-md overflow-hidden border-gray-200 dark:border-gray-700 box-border flex p-3 w-full text-black dark:text-white bg-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 dark:bg-gray-900 outline-none"
                    placeholder="Title"
                    v-model="
                      addConceptViewModel.concept.featuresBoxes.bestFeaturesBoxThreeTitle
                    "
                    required
                  />

                  <textarea
                    class="border-2 rounded-md overflow-hidden border-gray-200 dark:border-gray-700 box-border flex p-3 w-full text-black dark:text-white bg-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 dark:bg-gray-900 outline-none"
                    placeholder="Description"
                    v-model="
                      addConceptViewModel.concept.featuresBoxes
                        .bestFeaturesBoxThreeDescription
                    "
                    required
                  ></textarea>
                </div>
              </div>
            </div>
          </div>

          <!-- Gallery Section -->
          <div class="flex flex-col gap-4">
            <div class="flex flex-col gap-1 mb-1">
              <h3 class="text-lg text-primary font-medium">Gallery Section</h3>
              <hr class="border-none w-full h-[2px] bg-gray-200 dark:bg-gray-600" />
            </div>
            <div class="flex flex-col gap-2 w-full">
              <h6 class="font-medium text-sm text-gray-600 dark:text-gray-400">
                Gallery Pictures
              </h6>

              <FileUploader
                @updatePendingFiles="updatePendingFiles($event, 'galleries')"
                :allowMultiple="true"
              ></FileUploader>
              <span class="italic text-sm text-black dark:text-white"
                >* Notice: You can select multiple images for the gallery section by
                holding the 'Ctrl' key.</span
              >
            </div>
          </div>

          <!-- Maps Section -->
          <div class="flex flex-col gap-4">
            <div class="flex flex-col gap-1 mb-1">
              <h3 class="text-lg text-primary font-medium">Maps Section</h3>
              <hr class="border-none w-full h-[2px] bg-gray-200 dark:bg-gray-600" />
            </div>
            <div class="flex max-xl:flex-col xl:gap-5 gap-4">
              <div class="flex flex-col gap-2 w-full">
                <h6 class="font-medium text-sm text-gray-600 dark:text-gray-400">
                  Map 1
                </h6>
                <FileUploader
                  @updatePendingFiles="updatePendingFiles($event, 'map1')"
                ></FileUploader>
              </div>

              <div class="flex flex-col gap-2 w-full">
                <h6 class="font-medium text-sm text-gray-600 dark:text-gray-400">
                  Map 2
                </h6>
                <FileUploader
                  @updatePendingFiles="updatePendingFiles($event, 'map2')"
                ></FileUploader>
              </div>
            </div>
          </div>
        </div>

        <hr class="w-full border dark:border-gray-700" />
        <div class="wrapper-button flex justify-end mt-5">
          <button
            v-if="!isAdding"
            type="submit"
            class="text-white bg-primary rounded-md hover:bg-primary/80 transition-all duration-300 focus:outline-none py-2 px-6 w-fit"
          >
            Save
          </button>

          <button
            v-else
            disabled
            type="button"
            class="text-white bg-primary rounded-md opacity-60 cursor-default transition-all duration-300 focus:outline-none py-2 px-6 w-fit"
          >
            <svg
              role="status"
              class="inline w-4 h-4 mr-1 text-white animate-spin"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="#E5E7EB"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentColor"
              />
            </svg>
            Uploading...
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import FileUploader from "@/components/FileUploader.vue";
import axios from "axios";
import { Icon } from "@iconify/vue";
import Input from "@/components/Input.vue";

export default {
  components: {
    FileUploader,
    Input,
    Icon,
  },

  data() {
    return {
      addConceptViewModel: {
        projectImage: null,
        featuresBoxImage1: null,
        featuresBoxImage2: null,
        featuresBoxImage3: null,
        map1: null,
        map2: null,
        galleries: [],
        sliderPictures: [{ file: null, title: "" }],
        concept: {
          title: "",
          projectText: "",
          featuresBoxes: {
            bestFeaturesBoxOneTitle: "",
            bestFeaturesBoxOneDescription: "",
            bestFeaturesBoxTwoTitle: "",
            bestFeaturesBoxTwoDescription: "",
            bestFeaturesBoxThreeTitle: "",
            bestFeaturesBoxThreeDescription: "",
          },
        },
      },
      isAdding: false,
    };
  },

  methods: {
    updatePendingFiles: function (files, imageType, sliderIndex = 0) {
      console.log(files);
      if (files) {
        if (imageType == "featuresBoxImage1") {
          this.addConceptViewModel.featuresBoxImage1 = files[0]?.file;
        }

        if (imageType == "featuresBoxImage2") {
          this.addConceptViewModel.featuresBoxImage2 = files[0]?.file;
        }

        if (imageType == "featuresBoxImage3") {
          this.addConceptViewModel.featuresBoxImage3 = files[0]?.file;
        }

        if (imageType == "map1") {
          this.addConceptViewModel.map1 = files[0]?.file;
        }

        if (imageType == "map2") {
          this.addConceptViewModel.map2 = files[0]?.file;
        }

        if (imageType == "slider") {
          this.addConceptViewModel.sliderPictures[sliderIndex].file = files[0]?.file;
        }

        if (imageType == "projectImage") {
          this.addConceptViewModel.projectImage = files[0]?.file;
        }

        if (imageType == "galleries") {
          this.addConceptViewModel.galleries = files;
        }
      }
    },
    addSlider: function () {
      this.addConceptViewModel.sliderPictures.push({ file: null, title: "" });
    },
    addConcept: async function () {
      var bodyFormData = new FormData();

      for (let index = 0; index < this.addConceptViewModel.sliderPictures.length; index++) {
        const element = this.addConceptViewModel.sliderPictures[index];

        if (!element.file) {
          this.$store.commit("showToast", {
            id: "myToast",
            type: "error",
            title: `Slider Image ${index + 1} Is Required!`,
            body: `You must choice any image for slider image ${index + 1}!`,
          });
          return;
        }

        bodyFormData.append(
          "SliderViewModel.ImageFiles",
          element.file,
          element.file.name
        );

        bodyFormData.append("SliderViewModel.Descriptions", element.title);
      }

      bodyFormData.append("Title", this.addConceptViewModel.concept.title);
      bodyFormData.append("ProjectText", this.addConceptViewModel.concept.projectText);

      bodyFormData.append(
        "BestFeaturesBoxOneTitle",
        this.addConceptViewModel.concept.featuresBoxes.bestFeaturesBoxOneTitle
      );

      bodyFormData.append(
        "BestFeaturesBoxOneDescription",
        this.addConceptViewModel.concept.featuresBoxes.bestFeaturesBoxOneDescription
      );

      bodyFormData.append(
        "BestFeaturesBoxTwoTitle",
        this.addConceptViewModel.concept.featuresBoxes.bestFeaturesBoxTwoTitle
      );

      bodyFormData.append(
        "BestFeaturesBoxTwoDescription",
        this.addConceptViewModel.concept.featuresBoxes.bestFeaturesBoxTwoDescription
      );

      bodyFormData.append(
        "BestFeaturesBoxThreeTitle",
        this.addConceptViewModel.concept.featuresBoxes.bestFeaturesBoxThreeTitle
      );

      bodyFormData.append(
        "BestFeaturesBoxThreeDescription",
        this.addConceptViewModel.concept.featuresBoxes.bestFeaturesBoxThreeDescription
      );

      if (!this.addConceptViewModel.projectImage) {
        this.$store.commit("showToast", {
          id: "myToast",
          type: "error",
          title: "Project Image Is Required!",
          body: "You must choice any image for project image!",
        });
        return;
      }

      bodyFormData.append(
        "ProjectImageFile",
        this.addConceptViewModel.projectImage,
        this.addConceptViewModel.projectImage.name
      );

      if (!this.addConceptViewModel.featuresBoxImage1) {
        this.$store.commit("showToast", {
          id: "myToast",
          type: "error",
          title: "Features Box Image 1 Is Required!",
          body: "You must choice any image for features box image 1!",
        });
        return;
      }

      bodyFormData.append(
        "BestFeaturesBoxOneImageFile",
        this.addConceptViewModel.featuresBoxImage1,
        this.addConceptViewModel.featuresBoxImage1.name
      );

      if (!this.addConceptViewModel.featuresBoxImage2) {
        this.$store.commit("showToast", {
          id: "myToast",
          type: "error",
          title: "Features Box Image 2 Is Required!",
          body: "You must choice any image for features box image 2!",
        });
        return;
      }

      bodyFormData.append(
        "BestFeaturesBoxTwoImageFile",
        this.addConceptViewModel.featuresBoxImage2,
        this.addConceptViewModel.featuresBoxImage2.name
      );

      if (!this.addConceptViewModel.featuresBoxImage3) {
        this.$store.commit("showToast", {
          id: "myToast",
          type: "error",
          title: "Features Box Image 3 Is Required!",
          body: "You must choice any image for features box image 3!",
        });
        return;
      }

      bodyFormData.append(
        "BestFeaturesBoxThreeImageFile",
        this.addConceptViewModel.featuresBoxImage3,
        this.addConceptViewModel.featuresBoxImage3.name
      );

      if (this.addConceptViewModel.galleries.length == 0) {
        this.$store.commit("showToast", {
          id: "myToast",
          type: "error",
          title: `Gallery Image Is Required!`,
          body: "You must choice one image at least for gallery images!",
        });
        return;
      }

      this.addConceptViewModel.galleries.forEach((element) => {
        bodyFormData.append("GalleryViewModel.Files", element.file, element.file.name);
      });

      if (!this.addConceptViewModel.map1) {
        this.$store.commit("showToast", {
          id: "myToast",
          type: "error",
          title: "Map Image 1 Is Required!",
          body: "You must choice any image for map image 1!",
        });
        return;
      }

      bodyFormData.append(
        "MapPictureFile1",
        this.addConceptViewModel.map1,
        this.addConceptViewModel.map1.name
      );

      if (!this.addConceptViewModel.map2) {
        this.$store.commit("showToast", {
          id: "myToast",
          type: "error",
          title: "Map Image 2 Is Required!",
          body: "You must choice any image for map image 2!",
        });
        return;
      }

      bodyFormData.append(
        "MapPictureFile2",
        this.addConceptViewModel.map2,
        this.addConceptViewModel.map2.name
      );

      

      this.isAdding = true;

      let options = {
        headers: {
          "Content-Type": "multipart/form-data",
          isNeedJwtAuth: true,
        },
      };

      await axios
        .post("Concepts", bodyFormData, options)
        .then((response) => {
          this.isAdding = false;

          console.log(response);

          window.scrollTo(0, 0);

          this.$store.commit("showToast", {
            id: "myToast",
            type: "success",
            title: "Add Successful!",
            body: "The adition was successful!",
          });
        })
        .catch((error) => {
          this.isAdding = false;
          if (error.response?.data?.isSuccess == false) {
            this.$store.commit("showToast", {
              id: "myToast",
              type: "error",
              title: "Add Failed!",
              body: error.response?.data?.messages[0],
            });
          }
          console.log(error);
          console.log(error.response);
        });
    },
    deleteSliderItem(index) {
      this.addConceptViewModel.sliderPictures.splice(index, 1);
    },
  },
};
</script>
